import {
  DomainTargeting,
  type UrlTargeting,
  UrlTargetingFields,
  UrlTargetingType,
} from '@openx/types/targeting/domains';
import type { TargetingProps } from '@openx/types/targeting/targeting';
import { noop } from '@openx/utils/noop';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useMemo, useState } from 'react';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer';
import { DomainsItemsEditOnly } from './components/DomainsItemsEditOnly';
import { DomainsReadOnly } from './DomainsReadOnly';
import { useResetDomainsToAccountDefault } from './hooks/useResetDomainsToAccountDefault';
import { applyUrlTargeting } from './utils/applyUrlTargeting';

const defaultState: UrlTargeting = {
  type: UrlTargetingType.whitelist,
  domain_targeting_option: DomainTargeting.ALL_VARIATIONS,
  urls: undefined,
  inventorylist_id: undefined,
  [UrlTargetingFields.domain_app_inventory_operator]: undefined,
};

export const Domains = memo(function Domains({
  targetingParams,
  onFieldUpdate = noop,
  name,
  readonly,
  field,
  accountUid,
}: TargetingProps<UrlTargeting>) {
  const initState = useMemo(() => (targetingParams ? cloneDeep(targetingParams) : defaultState), [targetingParams]);

  const [params, updateParams] = useState<UrlTargeting>(initState);
  const [userChangedInitialValue, setUserChangedInitialValue] = useState(false);
  const [isApplyEnabled, setApplyEnabled] = useState(true);

  const isDataUpdated = useMemo(() => !isEqual(initState, params), [initState, params]);

  const onFieldUpdateExtended = useCallback(
    (...args: Parameters<typeof onFieldUpdate>) => {
      setUserChangedInitialValue(true);
      onFieldUpdate(...args);
    },
    [onFieldUpdate]
  );

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => onFieldUpdateExtended(field, applyUrlTargeting(params)),
    restoreParams: () => updateParams(initState),
  });

  const onRemoveAll = useCallback(() => {
    onFieldUpdateExtended(field, null);
    updateParams(defaultState);
  }, [field, onFieldUpdateExtended]);

  const { accountDefaultState, handleResetToAccountDefault, inheritanceInfoComponent, shouldDisplayResetButton } =
    useResetDomainsToAccountDefault({
      accountUid,
      field,
      initState,
      onFieldUpdate: onFieldUpdateExtended,
      onRemoveAll,
      updateParams,
    });

  const isResetToAccountEnabled = useMemo(() => {
    if (!params.urls?.length && !accountDefaultState.urls?.length) {
      return false;
    }

    const currentState: UrlTargeting = {
      ...params,
      urls: params.urls?.length ? [...params.urls].sort() : undefined,
    };

    return !isEqual(currentState, accountDefaultState);
  }, [accountDefaultState, params]);

  return (
    <>
      <DomainsReadOnly
        name={name}
        readonly={readonly}
        targetingParams={targetingParams}
        onOpenDrawerClick={onDrawerOpen}
        onRemoveClick={onRemoveAll}
        infoComponent={inheritanceInfoComponent}
        accountUid={accountUid}
        userChangedInitialValue={userChangedInitialValue}
        field={field}
      />

      {isOpen && (
        <TargetingDrawer
          name={name}
          onClose={onDrawerClose}
          onApply={onDrawerApply}
          isApplyDisabled={!(isApplyEnabled && isDataUpdated)}
        >
          <DomainsItemsEditOnly
            targetingParams={params}
            readonly={false}
            onChange={updateParams}
            handleReset={handleResetToAccountDefault}
            shouldDisplayResetButton={shouldDisplayResetButton}
            isResetToAccountEnabled={isResetToAccountEnabled}
            accountUid={accountUid}
            setApplyEnabled={setApplyEnabled}
          />
        </TargetingDrawer>
      )}
    </>
  );
});
